// Path: src/components/chat/ChatPa.tsx
// Removed prompts, updated SettingsButton to icon-only, added chat next to Community with online users.
// Improved UI layout to match screenshot, added admin email/notification for new messages.
// Ensured mobile responsiveness and SEO-friendly design.
// 06/07/25 8:07 PM +07
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useFirebase } from '../dev/Firebase/Firebase';
import { useUserStore } from '../../stores/UserStore';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, orderBy, doc, updateDoc, limit } from 'firebase/firestore';
import { getDatabase, ref, onValue } from 'firebase/database';
import MessageList from './MessageList';
import InputField from './InputField';
import ErrorDisplay from './ErrorDisplay';
import LoadingIndicator from './LoadingIndicator';
import { Button } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

const ChatPaContainer = styled.ul`
  width: 64px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  border-left: 1px solid #e5e7eb;
  padding-left: 1px;
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1300;
  @media (max-width: 768px) {
    bottom: 15px;
    right: calc(20px + env(safe-area-inset-right, 0px));
    width: 56px;
  }
`;

const ChatPaItem = styled.li`
  width: 56px;
  height: 56px;
  overflow: hidden;
  transition: all 500ms ease;
  position: relative;
  background: #f0fdf4;
  border: 2px solid #bbf7d0;
  border-radius: 8px;
  &.expanded {
    width: 256px;
    height: 400px;
    transform: translateX(-200px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    @media (max-width: 768px) {
      width: min(200px, 80vw);
      height: 350px;
      transform: translateX(calc(-100% + 48px));
    }
  }
  &:hover, &:focus-within {
    width: 256px;
    transform: translateX(-200px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    @media (max-width: 768px) {
      width: min(200px, 80vw);
      transform: translateX(calc(-100% + 48px));
    }
  }
`;

const ChatPaButton = styled.button`
  display: flex;
  width: 100%;
  height: 100%;
  cursor: pointer;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem;
  text-align: left;
  color: #16a34a;
  transition: all 150ms ease;
  background: transparent;
  border: none;
  &:active {
    transform: scale(0.95);
  }
  .icon-wrapper {
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    border: 2px solid #bbf7d0;
    background-color: #f0fdf4;
  }
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
  .label {
    font-weight: 600;
  }
`;

const ChatPaContent = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  transition: grid-template-rows 500ms ease, height 500ms ease;
  &.expanded {
    grid-template-rows: 2fr 1fr;
  }
`;

const ChatPaInput = styled.div`
  position: relative;
  .input-field {
    height: 32px;
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    padding-left: 8px;
    font-size: 14px;
  }
  .send-button {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: #2563eb;
    &:hover {
      color: #1d4ed8;
    }
  }
`;

const ChatTypeSelector = styled.div`
  padding: 0.5rem;
  display: flex;
  justify-content: center;
  gap: 1rem;
  background: #f0fdf4;
  border-bottom: 1px solid #e5e7eb;
`;

const SettingsButton = styled(Button)`
  position: absolute;
  bottom: 70px;
  right: 20px;
  opacity: 0;
  transition: opacity 300ms ease;
  &.expanded {
    opacity: 1;
  }
  @media (max-width: 768px) {
    right: calc(20px + env(safe-area-inset-right, 0px));
  }
  span {
    display: none; /* Hide text, keep icon */
  }
  .anticon {
    font-size: 18px;
  }
`;

const ChatPa: React.FC = () => {
  const { firestore, database, functions, auth } = useFirebase();
  const userStore = useUserStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  const [hasSentFirstMessage, setHasSentFirstMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [chatType, setChatType] = useState<'olsSupport' | 'community'>('olsSupport');

  // Set userId from Firebase Auth
  useEffect(() => {
    if (auth.currentUser) {
      setUserId(auth.currentUser.uid);
      console.log('ChatPa: Set userId from auth:', auth.currentUser.uid);
    } else {
      console.log('ChatPa: Waiting for auth user...');
    }
  }, [auth.currentUser]);

  // Monitor online users via Realtime Database
  useEffect(() => {
    const db = getDatabase();
    const statusRef = ref(db, 'status');
    const unsubscribe = onValue(statusRef, (snapshot) => {
      if (snapshot.exists()) {
        setOnlineUsers(snapshot.numChildren());
        console.log(`Verification: Live user count updated to ${snapshot.numChildren()}`);
      } else {
        setOnlineUsers(0);
      }
    }, (err) => setError(`Network error: ${err.message}`));
    return () => unsubscribe();
  }, []);

  // Query chats for the authenticated user
  const chatQuery = useCallback(() => {
    if (!userId || !firestore) return null;
    return query(
      collection(firestore, 'chats'),
      where('userId', '==', userId),
      where('status', '!=', 'closed'),
      limit(1)
    );
  }, [firestore, userId]);

  // Listen for chat updates
  useEffect(() => {
    if (!userId || !firestore || !chatQuery()) return;

    const unsubscribe = onSnapshot(chatQuery()!, (querySnapshot) => {
      if (!querySnapshot.empty) {
        const chatDoc = querySnapshot.docs[0];
        setChatId(chatDoc.id);
        if (chatDoc.data().unreadByUser && !isExpanded) {
          setUnreadCount((prev) => prev + 1);
        }
      } else {
        setChatId(null);
      }
    }, (err) => setError(`Failed to fetch chat: ${err.message}`));
    return () => unsubscribe();
  }, [userId, firestore, isExpanded, chatQuery]);

  // Listen for messages in the active chat
  useEffect(() => {
    if (!chatId || !firestore) {
      setMessages([]);
      return;
    }
    const messagesQuery = query(
      collection(firestore, `chats/${chatId}/messages`),
      orderBy('timestamp', 'asc'),
      limit(100)
    );
    const unsubscribe = onSnapshot(messagesQuery, (querySnapshot) => {
      const fetchedMessages = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        type: doc.data().senderId === userId ? 'user' : 'admin'
      }));
      setMessages(fetchedMessages);
      if (fetchedMessages.length > 0 && fetchedMessages[fetchedMessages.length - 1].senderId !== userId) {
        notifyAdmin(fetchedMessages[fetchedMessages.length - 1].text);
      }
    }, (err) => setError(`Failed to fetch messages: ${err.message}`));
    return () => unsubscribe();
  }, [chatId, firestore, userId]);

  const handleToggleChat = useCallback(() => {
    setIsExpanded((prev) => {
      const currentlyOpening = !prev;
      if (currentlyOpening && chatId) {
        setUnreadCount(0);
        const chatRef = doc(firestore, 'chats', chatId);
        updateDoc(chatRef, { unreadByUser: false });
      }
      return currentlyOpening;
    });
  }, [chatId, firestore]);

  const handleSendMessage = useCallback(async (message: string) => {
    if (!message.trim() || !firestore || !userId) return;
    if (!hasSentFirstMessage && message.trim().length > 20) {
      setError("Your first message must be 20 characters or less.");
      return;
    }

    setIsLoading(true);
    try {
      let currentChatId = chatId;
      if (!currentChatId) {
        const chatRef = await addDoc(collection(firestore, 'chats'), {
          userId,
          status: 'open',
          lastMessage: message,
          lastUpdated: serverTimestamp(),
          unreadByAdmin: true,
          createdAt: serverTimestamp(),
        });
        currentChatId = chatRef.id;
        setChatId(currentChatId);
      }

      await addDoc(collection(firestore, `chats/${currentChatId}/messages`), {
        text: message,
        senderId: userId,
        timestamp: serverTimestamp(),
        type: 'user'
      });
      const chatRef = doc(firestore, 'chats', currentChatId);
      await updateDoc(chatRef, {
        lastMessage: message,
        lastUpdated: serverTimestamp(),
        unreadByAdmin: true,
      });
      setNewMessage('');
      setIsAwaitingResponse(true);
      if (!hasSentFirstMessage) setHasSentFirstMessage(true);
    } catch (error) {
      setError(`Error sending message: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [newMessage, chatId, firestore, userId, hasSentFirstMessage]);

  const clearChatHistory = () => {
    setMessages([]);
    setError(null);
    setChatId(null);
  };

  const notifyAdmin = async (message: string) => {
    const notifyAdminFunction = functions.httpsCallable('notifyAdmin');
    try {
      await notifyAdminFunction({ message, userId });
      console.log('Verification: Admin notification sent');
    } catch (error) {
      console.error('Verification: Failed to notify admin:', error);
    }
  };

  return (
    <ChatPaContainer>
      <ChatPaItem className={isExpanded ? 'expanded' : ''}>
        <ChatPaButton onClick={handleToggleChat} aria-label={isExpanded ? 'Close chat' : 'Open chat'}>
          <div className="icon-wrapper">
            <svg stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" strokeLinejoin="round" strokeLinecap="round" />
            </svg>
          </div>
          <div className="label">Chat</div>
        </ChatPaButton>
        <ChatPaContent className={isExpanded ? 'expanded' : ''}>
          <ChatTypeSelector>
            <Button onClick={() => setChatType('olsSupport')}>OLS Support</Button>
            <Button onClick={() => setChatType('community')}>Community ({onlineUsers} online)</Button>
          </ChatTypeSelector>
          <MessageList
            messages={messages}
            onlineUsers={onlineUsers}
            isExpanded={isExpanded}
            isAwaitingResponse={isAwaitingResponse}
            chatType={chatType}
          />
          <ChatPaInput>
            <InputField
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              handleSendMessage={handleSendMessage}
              isInputDisabled={!chatId && !userId}
              hasSentFirstMessage={hasSentFirstMessage}
              prompts={[]}
            />
          </ChatPaInput>
        </ChatPaContent>
      </ChatPaItem>
      <ErrorDisplay error={error} />
      <LoadingIndicator isLoading={isLoading} />
      <SettingsButton className={isExpanded ? 'expanded' : ''} icon={<SettingOutlined />} onClick={clearChatHistory} />
    </ChatPaContainer>
  );
};

export default React.memo(ChatPa);